import axios from "axios";

// The access token lives here and nowhere else. Not localStorage, not
// sessionStorage, not a cookie, not the store: a module variable is the only
// place an XSS cannot read it back out of after the fact. It does not survive a
// reload either, which is the point - the refresh cookie is what does.
let accessToken = null;

// Everything that has to move when the token moves. The socket is the only
// subscriber today: FE-6 hung that work off the shared request interceptor, so a
// token reached the socket only when this tab made an HTTP call - and a
// cross-tab refresh delivers one with no local traffic behind it, leaving a
// passive tab presenting a credential BE-8 sweeps after KC-1.
const accessTokenListeners = new Set();

export const onAccessTokenChange = (listener) => {
  accessTokenListeners.add(listener);
  return () => accessTokenListeners.delete(listener);
};

// Set once a session end has been handed to a subscriber, so the siblings of the
// request that discovered it cannot act on it again. Cleared when a token arrives,
// which is the only thing that makes a new session end possible.
let sessionEndHandled = false;

export const setAccessToken = (token) => {
  const next = token || null;
  if (next === accessToken) {
    return;
  }
  if (next) {
    sessionEndHandled = false;
  }
  accessToken = next;
  // Before the subscribers, so the socket and the schedule are never briefly out
  // of step with each other.
  scheduleProactiveRefresh(next);
  accessTokenListeners.forEach((listener) => {
    try {
      listener(next);
    } catch (listenerError) {
      // A subscriber must not turn a successful refresh into a failed one: this
      // runs inside refreshAccessToken, whose rejection path ends the session.
      console.error("An access token subscriber failed", listenerError);
    }
  });
};

export const getAccessToken = () => accessToken;

// The other half of the subscription pattern above, and it exists for the same
// reason: this module cannot import the action layer, because userActionCreators
// imports it. A session that has genuinely ended is announced here and the modal
// subscribes, rather than four action files each guessing from a 401 the
// interceptor has already refreshed and replayed (F-24).
const sessionEndedListeners = new Set();

export const onSessionEnded = (listener) => {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
};

const notifySessionEnded = () => {
  sessionEndedListeners.forEach((listener) => {
    try {
      listener();
    } catch (listenerError) {
      console.error("A session-end subscriber failed", listenerError);
    }
  });
  return sessionEndedListeners.size > 0;
};

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
});
axiosInstance.interceptors.request.use((request) => {
  // add auth header with jwt if account is logged in
  if (accessToken) {
    // Set on the config, not headers.common: dispatchRequest flattens with
    // config.headers last, so this is the half of the merge that wins - the same
    // order the replay path depends on - and it survives an axios 1.x migration.
    request.headers["x-access-token"] = accessToken;
  }
  return request;
});

const responseSuccessHandler = (response) => {
  return response;
};

// BE-3 answers 401 with two different bodies that mean opposite things, and the
// whole design depends on the client telling them apart: "token expired" is
// retryable - refresh and replay - while "session ended" is terminal. Returning
// the terminal one from /auth/refresh is what stops a client looping (F-18).
const TOKEN_EXPIRED = "token expired";
const SESSION_ENDED = "session ended";

// One refresh at a time, shared by every caller waiting on it. Six concurrent
// 401s must produce one call to /auth/refresh and five awaits of it: after KC-2
// the refresh token rotates, so a second concurrent refresh replays a token
// keycloak has already retired and reuse detection reads that as theft.
let refreshInFlight = null;

// Single-flight stops at the tab boundary, and the cookie does not: every tab
// on this origin refreshes against the same one. After KC-2 that token rotates,
// so two tabs refreshing at once means the slower one presents a token keycloak
// has already retired, reuse detection reads the replay as theft and the
// session dies in both of them (H-4). One tab takes the lock, refreshes, and
// announces the result; the rest find the new token waiting and ask for
// nothing.
const REFRESH_LOCK = "cytailor-auth-refresh";
const REFRESH_CHANNEL = "cytailor-auth-refresh";
const ACCESS_TOKEN_ANNOUNCEMENT = "access-token";
const SESSION_ENDED_ANNOUNCEMENT = "session-ended";

const refreshChannel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(REFRESH_CHANNEL);

if (refreshChannel) {
  refreshChannel.onmessage = (event) => {
    if (event?.data?.type === SESSION_ENDED_ANNOUNCEMENT) {
      // One tab found out; the rest were going to keep rendering a signed-in UI
      // until their own next request failed (F-70). Not re-announced from here:
      // a channel never echoes to its own sender, so two tabs answering each
      // other would never stop.
      endSession({ announce: false });
      return;
    }

    if (
      event?.data?.type !== ACCESS_TOKEN_ANNOUNCEMENT ||
      !event?.data?.token
    ) {
      return;
    }
    // Still only ever in memory: a channel message is as unpersisted in the tab
    // that receives it as the module variable was in the tab that sent it. The
    // subscribers above are what carry it on to the socket.
    setAccessToken(event.data.token);
  };
}

const announceAccessToken = (token) => {
  if (refreshChannel && token) {
    refreshChannel.postMessage({
      type: ACCESS_TOKEN_ANNOUNCEMENT,
      token,
    });
  }
};

const announceSessionEnded = () => {
  if (refreshChannel) {
    refreshChannel.postMessage({ type: SESSION_ENDED_ANNOUNCEMENT });
  }
};

// The web lock is held for exactly as long as the callback runs and is released
// by the browser if the tab holding it disappears, which no localStorage lock
// can promise. Where it is missing the refresh still works and still announces;
// the tabs simply are not serialised against each other, which is where this
// branch started.
const withRefreshLock = (run) =>
  navigator?.locks?.request ? navigator.locks.request(REFRESH_LOCK, run) : run();

// A connection that hangs rather than failing is the one outage shape nothing
// else here catches, and the lock changes what it costs: it used to stall this
// tab's refresh, and it would now hold the lock - so every tab's refresh - for
// as long as the browser's own network timeout, which is minutes. Bounding the
// call bounds the lock. axios rejects a timeout with no `response`, so the
// interceptor's 401 test is false and the session survives: the original
// request fails and the next 401 tries again, which is the treatment an
// unreachable keycloak already gets (F-40).
//
// Deliberately no AbortSignal on the lock wait to go with it. Giving up on the
// wait can only mean refreshing unserialised, which is the rotation race this
// whole module exists to prevent (H-4) - and it would fire in exactly the slow
// conditions that make a stale replay most likely. The holder can no longer
// outlast the timeout, so the wait is bounded by the thing that matters.
const REFRESH_TIMEOUT_MS = 10000;

export const refreshAccessToken = () => {
  if (!refreshInFlight) {
    const refreshedFrom = accessToken;
    refreshInFlight = withRefreshLock(() => {
      // Queued behind another tab that has already refreshed and announced the
      // result. That announcement is the token this tab was about to ask for,
      // so asking again is the second rotation the lock exists to prevent.
      if (accessToken && accessToken !== refreshedFrom) {
        return Promise.resolve(accessToken);
      }
      // Deliberately not axiosInstance. This call authenticates with the cookie
      // rather than a bearer token, and it has to stay outside the response
      // interceptor so that a refused refresh can never re-enter it. withCredentials
      // belongs here and on /auth/logout only - putting it on the shared instance
      // would force every service to answer Allow-Credentials or the browser drops
      // the response, even on calls that send no cookie at all.
      return axios
        .post(`${process.env.REACT_APP_API_URL}/auth/refresh`, null, {
          withCredentials: true,
          timeout: REFRESH_TIMEOUT_MS,
        })
        .then((response) => {
          setAccessToken(response?.data?.token);
          announceAccessToken(response?.data?.token);
          return response?.data?.token;
        });
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

function endSession({ announce = true } = {}) {
  // Whether there was a session to lose, read before the token is dropped. A
  // refused refresh at boot is an anonymous visitor on the public landing page -
  // FE-3 asks for one on every load that finds localStorage.user - and telling
  // them their session expired would be a lie. Only a token that was in memory
  // says a live session just ended.
  // Every request that was in flight when the session ended fails, and each one
  // arrives here. Only the first finds a token in memory; the rest would see
  // hadSession false, skip the modal branch and fall through to the redirect -
  // navigating away from the dialog their own sibling had just put on screen.
  if (sessionEndHandled) {
    return;
  }

  const hadSession = accessToken !== null;

  setAccessToken(null);

  if (hadSession) {
    if (announce) {
      announceSessionEnded();
    }
    // The modal is what the user sees, and it is blocking, so the tokenless app
    // behind it is never reachable: the redirect is the modal's to make once the
    // sign-out has been acknowledged. Bouncing to /login from here would take the
    // explanation off the screen before it was read.
    //
    // Storage is deliberately NOT cleared on this path.  The authenticated tree is
    // still mounted and still rendering from localStorage.user; dropping it out
    // from under a live component throws, react unmounts the tree, and the modal
    // goes with it - a blank page instead of an explanation.  The teardown belongs
    // to acknowledgeSessionEnded, which does it at the moment it navigates away.
    if (notifySessionEnded()) {
      sessionEndHandled = true;
      return;
    }
  }

  // Nobody is subscribed, so nobody can be told and no modal will appear: redirect
  // from here, and take the keys the boot gate reads with it (F-62).  Safe at this
  // point because the navigation tears the tree down anyway.
  localStorage.removeItem("user");
  localStorage.removeItem("orgaId");

  if (window.location.pathname !== "/login") {
    window.location = "/login";
  }
}

// FE-6 left the token following HTTP traffic: nothing renewed it except a 401,
// and a 401 needs a request. A tab waiting on a report makes none, so after
// KC-1 its token aged out at 300s, BE-8 swept the notification socket, and the
// event the user was waiting for was delivered to a connection that no longer
// existed. Renewing on a schedule is what keeps the socket alive across an idle
// wait - which is the only time a realtime notification is the point.
//
// FE-6 ruled this out because it would have held an idle tab's keycloak session
// open past ssoSessionIdleTimeout. That timeout has since been raised to equal
// ssoSessionMaxLifespan and no longer fires independently, so the objection is
// gone: the absolute 24h ceiling is unaffected by activity, and the daily
// sign-out still arrives on time.
const PROACTIVE_REFRESH_LEAD_MS = 60000;
const PROACTIVE_REFRESH_MIN_DELAY_MS = 5000;
const PROACTIVE_REFRESH_RETRY_MS = 30000;

let proactiveRefreshTimer = null;
let accessTokenExpiry = null;

// The exp claim, read rather than verified. It decides when to ask for a new
// token and nothing else, so a tampered one costs a badly timed refresh and no
// more - the server is still the only thing that decides whether a token is
// good. Reading it beats hardcoding 300s: the schedule follows whatever
// accessTokenLifespan is set to, including the next time it changes.
const tokenExpiry = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const exp = JSON.parse(atob(padded))?.exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (decodeError) {
    // Not a JWT this build can read. Failing closed here means falling back to
    // exactly the behaviour that shipped - refresh on 401, replay - so the cost
    // is the idle socket and nothing else.
    return null;
  }
};

const clearProactiveRefresh = () => {
  if (proactiveRefreshTimer !== null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

const runProactiveRefresh = () => {
  clearProactiveRefresh();
  if (!accessToken) {
    return;
  }
  // Shared single-flight, and that is what makes this safe across tabs: every
  // tab's timer fires at roughly the same moment, one takes the FE-5 lock and
  // refreshes, and the rest find the announced token already in memory when
  // their turn comes and ask for nothing (H-4).
  refreshAccessToken().catch((refreshError) => {
    if (refreshError?.response?.status === 401) {
      endSession();
      return;
    }
    // Unreachable keycloak rather than a session that ended (F-40). Keep the
    // session and come back shortly - BE-8 allows a grace window past expiry,
    // and the 401 path is still underneath this if the retry is too late.
    if (accessToken) {
      proactiveRefreshTimer = setTimeout(
        runProactiveRefresh,
        PROACTIVE_REFRESH_RETRY_MS
      );
    }
  });
};

function scheduleProactiveRefresh(token) {
  clearProactiveRefresh();
  accessTokenExpiry = token ? tokenExpiry(token) : null;
  if (accessTokenExpiry === null) {
    return;
  }
  // Floored, so a token that arrives already inside the lead window asks once
  // rather than immediately and in a loop.
  const delay = Math.max(
    accessTokenExpiry - Date.now() - PROACTIVE_REFRESH_LEAD_MS,
    PROACTIVE_REFRESH_MIN_DELAY_MS
  );
  proactiveRefreshTimer = setTimeout(runProactiveRefresh, delay);
}

// A timer does not run while the machine is asleep, and a hidden tab's timers
// are throttled. Either way a tab can come back holding a token that expired
// while it was away, with its socket long since swept. Becoming visible again
// is the moment to find out; the schedule covers everything else.
if (typeof document !== "undefined" && document.addEventListener) {
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState !== "visible" ||
      !accessToken ||
      accessTokenExpiry === null
    ) {
      return;
    }
    if (accessTokenExpiry - Date.now() <= PROACTIVE_REFRESH_LEAD_MS) {
      runProactiveRefresh();
    }
  });
}

const responseErrorHandler = async (error) => {
  const request = error?.config;
  const status = error?.response?.status;
  const error_ = error?.response?.data?.error_;

  // Terminal. No refresh, no retry - that is the entire reason BE-3 answers
  // something other than "token expired" here.
  if (status === 401 && error_ === SESSION_ENDED) {
    endSession();
    return Promise.reject(error);
  }

  if (
    status === 401 &&
    error_ === TOKEN_EXPIRED &&
    request &&
    !request._retried
  ) {
    request._retried = true;

    let token;
    try {
      token = await refreshAccessToken();
    } catch (refreshError) {
      if (refreshError?.response?.status === 401) {
        endSession();
      }
      // Anything else - a 500 above all - means keycloak is unreachable, not that
      // the session ended (F-40). Leave the session alone, fail the original
      // request, and let the next 401 try again.
      return Promise.reject(refreshError);
    }

    // dispatchRequest flattens headers with config.headers last, so the stale
    // x-access-token already on this config would win over the fresh one the
    // request interceptor writes into headers.common. Set it on the config.
    if (token) {
      request.headers["x-access-token"] = token;
    }
    return axiosInstance(request);
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.response.use(
  (response) => responseSuccessHandler(response),
  (error) => responseErrorHandler(error)
);

export default axiosInstance;
